import React from 'react'
import { useTeacherParentUpdates } from '@/hooks/useTeacherParentUpdates'

type Props = {
  onSelectStudent?: (studentId: string) => void
}

function formatKind(kind: string | null) {
  if (!kind) return null
  return kind.replace(/_/g, ' ')
}

export const ParentUpdatesCard: React.FC<Props> = ({ onSelectStudent }) => {
  const { data, loading, error } = useTeacherParentUpdates()
  const items = data ?? []

  return (
    <section className="rounded-2xl bg-white/85 p-6 shadow-soft backdrop-blur">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-cocoa">Parent updates</h3>
          <p className="text-sm text-cocoa/70">
            Latest notes from guardians across your classrooms (last 50 entries).
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {loading && <p className="text-sm text-cocoa/60">Loading parent updates…</p>}
        {error && !loading && <p className="text-sm text-brick">Couldn&apos;t load updates: {error}</p>}

        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-cocoa/60">
            No recent parent updates yet. When guardians share context about their students, you&apos;ll see it here.
          </p>
        )}

        {!loading && !error && items.length > 0 && (
          <ul className="divide-y divide-white/60 rounded-xl border border-white/60 bg-white/60">
            {items.slice(0, 10).map((student) => {
              const latest = student.updates[0]
              if (!latest) return null

              const date = new Date(latest.created_at)
              const timeLabel = date.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })

              return (
                <li key={`${student.student_id}-${latest.id}`} className="p-3">
                  <button
                    type="button"
                    className="flex w-full flex-col gap-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brick/70 disabled:cursor-default"
                    onClick={onSelectStudent ? () => onSelectStudent(student.student_id) : undefined}
                    disabled={!onSelectStudent}
                  >
                    <div className="flex flex-wrap items-center gap-2 text-sm text-cocoa/80">
                      <span className="font-semibold text-cocoa">{student.student_name}</span>
                      <span className="text-xs text-cocoa/70">{timeLabel}</span>
                      {formatKind(latest.kind) ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] uppercase tracking-wide text-amber-900">
                          {formatKind(latest.kind)}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-cocoa/90">
                      {latest.note || <span className="text-cocoa/60">No note provided.</span>}
                    </p>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}
